import React, { useState } from 'react';
import { Eye, KeyRound, PlugZap, RefreshCw } from 'lucide-react';
import { AppChrome, toolLogo } from './AppChrome';

/**
 * The claim of this pillar is oversight: one place that shows which company
 * tools are reachable, and which AI surfaces can reach them. A catalogue of
 * 4,000 logos does not say that — a map of tool → surface does.
 */

type SurfaceId = 'web' | 'slack' | 'chatgpt' | 'claude' | 'grok';

// Third-party surfaces first, Giga's own last: the list reads as "everywhere
// else, plus the two places Giga itself runs".
const SURFACES: Array<{
  id: SurfaceId;
  name: string;
  slug: string;
  note: string;
}> = [{
  id: 'chatgpt',
  name: 'ChatGPT',
  slug: 'chatgpt',
  note: 'Via Giga connector'
}, {
  id: 'claude',
  name: 'Claude Code',
  slug: 'claude',
  note: 'Engineering only'
}, {
  id: 'grok',
  name: 'Grok bot',
  slug: 'grok',
  note: 'Growth only'
}, {
  id: 'web',
  name: 'Giga for web',
  slug: 'giga',
  note: 'Company-wide'
}, {
  id: 'slack',
  name: 'Giga for Slack',
  slug: 'giga',
  note: 'Company-wide'
}];
const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png';

/** Giga's own surfaces use the black brand mark rather than a favicon. */
function SurfaceIcon({
  slug,
  className
}: {
  slug: string;
  className: string;
}) {
  if (slug === 'giga') {
    return <img src={GIGA_MARK} alt="" aria-hidden="true" className={`${className} [filter:brightness(0)]`} data-id="element-388" data-mp="26jhk11l96iy7" />;
  }
  return <img src={toolLogo(slug)} alt="" aria-hidden="true" className={`${className} object-contain`} data-id="element-389" data-mp="21o27qe2w8v3w" />;
}
type Node = {
  slug: string;
  name: string;
  accounts: number;
  surfaces: SurfaceId[];
};

// Workspace-wide view: every connected tool, how many accounts sit behind it,
// and which AI surfaces are allowed to use it.
const BY_SURFACE: Node[] = [{
  slug: 'slack',
  name: 'Slack',
  accounts: 3,
  surfaces: ['chatgpt', 'claude', 'web', 'slack']
}, {
  slug: 'gmail',
  name: 'Gmail',
  accounts: 5,
  surfaces: ['chatgpt', 'web', 'slack']
}, {
  slug: 'intercom',
  name: 'Intercom',
  accounts: 4,
  surfaces: ['chatgpt', 'web', 'slack']
}, {
  slug: 'salesforce',
  name: 'Salesforce',
  accounts: 2,
  surfaces: ['chatgpt', 'grok', 'web']
}, {
  slug: 'linear',
  name: 'Linear',
  accounts: 3,
  surfaces: ['claude', 'web', 'slack']
}, {
  slug: 'snowflake',
  name: 'Snowflake',
  accounts: 1,
  surfaces: ['claude', 'web']
}, {
  slug: 'hubspot',
  name: 'HubSpot',
  accounts: 2,
  surfaces: ['grok', 'web', 'slack']
}];
type Member = {
  name: string;
  role: string;
  initials: string;
  tint: string;
  nodes: Node[];
};
const TEAM: Member[] = [{
  name: 'Sarah Chen',
  role: 'Head of Ops · Admin',
  initials: 'SC',
  tint: '#134E4A',
  nodes: [{
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'gmail',
    name: 'Gmail',
    accounts: 2,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'salesforce',
    name: 'Salesforce',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'snowflake',
    name: 'Snowflake',
    accounts: 1,
    surfaces: ['chatgpt', 'web']
  }]
}, {
  name: 'Marcus Lee',
  role: 'Engineering',
  initials: 'ML',
  tint: '#3F3F46',
  nodes: [{
    slug: 'linear',
    name: 'Linear',
    accounts: 1,
    surfaces: ['claude', 'web', 'slack']
  }, {
    slug: 'github',
    name: 'GitHub',
    accounts: 2,
    surfaces: ['claude', 'web']
  }, {
    slug: 'sentry',
    name: 'Sentry',
    accounts: 1,
    surfaces: ['claude', 'slack']
  }, {
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Priya Raman',
  role: 'Growth',
  initials: 'PR',
  tint: '#7C2D12',
  nodes: [{
    slug: 'hubspot',
    name: 'HubSpot',
    accounts: 2,
    surfaces: ['grok', 'web', 'slack']
  }, {
    slug: 'mailchimp',
    name: 'Mailchimp',
    accounts: 1,
    surfaces: ['grok', 'web']
  }, {
    slug: 'ahrefs',
    name: 'Ahrefs',
    accounts: 1,
    surfaces: ['grok', 'chatgpt']
  }, {
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Tom Okafor',
  role: 'Finance',
  initials: 'TO',
  tint: '#1E3A5F',
  nodes: [{
    slug: 'stripe',
    name: 'Stripe',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'quickbooks',
    name: 'QuickBooks',
    accounts: 1,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'gmail',
    name: 'Gmail',
    accounts: 1,
    surfaces: ['chatgpt', 'web']
  }]
}, {
  name: 'Dana Whitfield',
  role: 'Support',
  initials: 'DW',
  tint: '#4C1D95',
  nodes: [{
    slug: 'intercom',
    name: 'Intercom',
    accounts: 2,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'linear',
    name: 'Linear',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['web', 'slack']
  }]
}];
const POINTS = [{
  icon: PlugZap,
  title: 'Anything your team already uses',
  copy: '4,000 tools in the directory, plus databases, API keys, and email-and-password logins for the ones without an API.'
}, {
  icon: KeyRound,
  title: 'Connected once, not per product',
  copy: 'Credentials live in Giga, not inside ChatGPT or Claude, so no single AI provider controls how your team reaches its tools.'
}, {
  icon: Eye,
  title: 'Owned centrally',
  copy: 'Every connection has an owner, a group, and a history. Admins can see what is connected and where company data can flow.'
}, {
  icon: RefreshCw,
  title: 'Kept alive',
  copy: 'Token refreshes are handled for you, and a one-click reconnect link goes out the moment something drops.'
}];
export function ConnectedAppsPillar() {
  const [tab, setTab] = useState<'surface' | 'person'>('surface');
  const [memberIndex, setMemberIndex] = useState(0);
  const nodes = tab === 'surface' ? BY_SURFACE : TEAM[memberIndex].nodes;
  const surfaces = tab === 'surface' ? SURFACES : SURFACES.filter(s => nodes.some(n => n.surfaces.includes(s.id)));
  const accounts = nodes.reduce((sum, n) => sum + n.accounts, 0);
  return <div data-id="element-390" data-mp="3ecri9m8it451">
      <div className="mx-auto max-w-3xl text-center" data-id="element-391" data-mp="3ec82ab2fkptn">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-392" data-mp="3edj2k0wo3fg0">
          Connected Apps
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-393" data-mp="2lo2dnd89agoj">
          Connect your tools once.
          <br data-id="element-394" data-mp="2qnot7tbk490i" />
          <span className="text-[#EA580C]" data-id="element-395" data-mp="2qnf56ba2obxg">Use them everywhere.</span>
        </h2>
        <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-396" data-mp="2un7eiq7aim3v">
          Giga is the connection layer between your company's software and its
          AI — and the one place you can see what reaches what.
        </p>
      </div>

      <div className="mx-auto mt-12 max-w-[1000px] md:mt-16" data-id="element-397" data-mp="26yxu9spq66eu">
        <AppChrome active="Connections" data-id="element-398" data-mp="26x3bol1xeqdx">
          <div className="px-5 py-5 md:px-7 md:py-6" data-id="element-399" data-mp="1zg6qvntoo0aw">
            <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-400" data-mp="1zdklnuu9lwvu">
              <div data-id="element-401" data-mp="1uh3m84hm7w3g">
                <h3 className="font-title text-[22px] font-medium tracking-tight text-[#2B211A]" data-id="element-402" data-mp="1ueheo4uq5gl8">
                  Connections
                </h3>
                <p className="mt-1 text-[13px] text-neutral-500" data-id="element-403" data-mp="1lgozr1sx0ayd">
                  {nodes.length} tools · {accounts} accounts ·{' '}
                  {surfaces.length} AI surfaces
                </p>
              </div>
              <span className="rounded-lg border border-[#E7E0D4] px-3 py-1.5 text-[12.5px] font-bold text-[#2B211A]" data-id="element-404" data-mp="1bpauedea8gxb">
                Connect New Tool
              </span>
            </div>

            <div className="mb-4 mt-5 flex items-center gap-5 border-b border-[#EFEAE1]" data-id="element-405" data-mp="12qfluunrmuqu">
              {([['surface', 'By surface'], ['person', 'By person']] as const).map(([id, label]) => <button key={id} type="button" onClick={() => setTab(id)} className={`-mb-px border-b-2 pb-2.5 text-[13px] font-semibold transition-colors ${tab === id ? 'border-[#2B211A] text-[#2B211A]' : 'border-transparent text-neutral-400 hover:text-[#2B211A]'}`} data-id="element-406" data-mp="sqosiwaxylsc">
                    {label}
                  </button>)}
            </div>

            {/* By person is the same map with a filter on top, not a different
             screen — so the mental model carries between the two tabs. */}
            {tab === 'person' && <div className="mb-4 flex flex-wrap gap-1.5" data-id="element-407" data-mp="1xwdjgjf5u1ym">
                {TEAM.map((m, i) => <button key={m.name} type="button" onClick={() => setMemberIndex(i)} aria-pressed={i === memberIndex} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors ${i === memberIndex ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#E7E0D4] text-[#4A3E35] hover:border-[#2B211A]/35'}`} data-id="element-408" data-mp="22oomvw9405vx" data-mpi={i}>
                    <span className="flex size-4 items-center justify-center rounded-full text-[8px] font-bold text-white" style={{
                backgroundColor: i === memberIndex ? 'rgba(255,255,255,0.22)' : m.tint
              }} data-id="element-409" data-mp="1jxnnx87zoacd">
                      {m.initials}
                    </span>
                    {m.name.split(' ')[0]}
                  </button>)}
              </div>}

            <AccessMap key={tab === 'surface' ? 'all' : TEAM[memberIndex].name} nodes={nodes} surfaces={surfaces} data-id="element-410" data-mp="1a7yngxc65znl" />
          </div>
        </AppChrome>
      </div>

      <div className="mx-auto mt-14 grid max-w-[1000px] gap-x-10 gap-y-8 md:mt-16 md:grid-cols-2" data-id="element-411" data-mp="1j9ewb0nc5tpp">
        {POINTS.map(({
        icon: Icon,
        title,
        copy
      }) => <div key={title} className="flex gap-4" data-id="element-412" data-mp="1t12fyfdni6gm">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-[#EA580C]/25 bg-white text-[#EA580C]" data-id="element-413" data-mp="1t2ly6bivarl1">
              <Icon size={17} data-id="element-414" data-mp="220plnddexyfh" />
            </span>
            <div data-id="element-415" data-mp="221h37ka6dvmr">
              <h3 className="font-title text-xl font-bold leading-snug text-[#2B211A]" data-id="element-416" data-mp="21yuwfmv3zk5l">
                {title}
              </h3>
              <p className="mt-1.5 text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-417" data-mp="3ny0k6mk5eoqy">
                {copy}
              </p>
            </div>
          </div>)}
      </div>
    </div>;
}
const ROW_H = 52;
const ROW_GAP = 8;
const STEP = ROW_H + ROW_GAP;
const CONNECTOR_W = 96;
const LABEL_H = 22;
type Focus = {
  kind: 'tool';
  slug: string;
} | {
  kind: 'surface';
  id: SurfaceId;
} | null;

/** Tools on the left, AI surfaces on the right, lines for what reaches what. */
function AccessMap({
  nodes,
  surfaces
}: {
  nodes: Node[];
  surfaces: Array<(typeof SURFACES)[number]>;
}) {
  const [focus, setFocus] = useState<Focus>(null);
  const listH = (n: number) => n * STEP - ROW_GAP;
  const height = Math.max(listH(nodes.length), listH(surfaces.length));
  const centerY = (index: number, count: number) => (height - listH(count)) / 2 + index * STEP + ROW_H / 2;
  const isLive = (slug: string, id: SurfaceId) => {
    if (!focus) return true;
    if (focus.kind === 'tool') return focus.slug === slug;
    return focus.id === id;
  };
  const toolDim = (slug: string) => focus?.kind === 'tool' ? focus.slug !== slug : focus?.kind === 'surface' ? !nodes.find(n => n.slug === slug)?.surfaces.includes(focus.id) : false;
  const surfaceDim = (id: SurfaceId) => focus?.kind === 'surface' ? focus.id !== id : focus?.kind === 'tool' ? !nodes.find(n => n.slug === focus.slug)?.surfaces.includes(id) : false;
  return <div className="flex flex-col gap-4 md:flex-row md:items-stretch md:gap-0" data-id="element-418" data-mp="1fqol6lnhql9y">
      <ul className="flex-1 space-y-2" onMouseLeave={() => setFocus(null)} data-id="element-419" data-mp="1koq85ho1f4qp">
        <ListLabel offset={(height - listH(nodes.length)) / 2} data-id="element-420" data-mp="1kqt5ynvfm25z">
          Connected tools
        </ListLabel>
        {nodes.map(n => <li key={n.slug} data-id="element-421" data-mp="1tq6enebqempn">
            <button type="button" onMouseEnter={() => setFocus({
          kind: 'tool',
          slug: n.slug
        })} onFocus={() => setFocus({
          kind: 'tool',
          slug: n.slug
        })} onClick={() => setFocus(f => f?.kind === 'tool' && f.slug === n.slug ? null : {
          kind: 'tool',
          slug: n.slug
        })} className={`flex w-full items-center gap-3 rounded-2xl border px-3.5 text-left transition-colors ${focus?.kind === 'tool' && focus.slug === n.slug ? 'border-[#EA580C]/45 bg-[#FFF8F3]' : 'border-[#EFEAE1] bg-white'} ${toolDim(n.slug) ? 'opacity-40' : ''}`} style={{
          height: ROW_H
        }} data-id="element-422" data-mp="1tod962cuu8j8">
              <img src={toolLogo(n.slug)} alt="" aria-hidden="true" className="size-[18px] shrink-0 object-contain" data-id="element-423" data-mp="1llf3cn87drl4" />
              <span className="min-w-0 flex-1" data-id="element-424" data-mp="1gmu5gugfbjzu">
                <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-425" data-mp="1gm28pgb3bg7z">
                  {n.name}
                </span>
                <span className="block truncate text-[11.5px] text-neutral-500" data-id="element-426" data-mp="1qcdkfp10mtpf">
                  {n.accounts} connected{' '}
                  {n.accounts === 1 ? 'account' : 'accounts'}
                </span>
              </span>
              {/* Only the third-party surfaces are listed here. Giga itself can
               always reach every connection, so its two marks added no
               information — and at this size they read as black dots. */}
              <span className="flex shrink-0 items-center -space-x-1" data-id="element-427" data-mp="2nwwzyoutcinp">
                {n.surfaces.filter(id => SURFACES.find(s => s.id === id)!.slug !== 'giga').map(id => <SurfaceIcon key={id} slug={SURFACES.find(s => s.id === id)!.slug} className="size-3.5 rounded-full bg-white ring-1 ring-[#EFEAE1]" data-id="element-428" data-mp="2dypnojbgyos1" />)}
              </span>
            </button>
          </li>)}
      </ul>

      {/* Fixed row heights let the connectors be drawn from indices, so no
       measurement pass is needed and the lines never drift. */}
      <div className="hidden shrink-0 md:block" style={{
      width: CONNECTOR_W
    }} aria-hidden="true" data-id="element-429" data-mp="1q83geu9ah8j3">
        <div style={{
        height: LABEL_H + ROW_GAP
      }} data-id="element-430" data-mp="1h987haev1kc3" />
        <svg width={CONNECTOR_W} height={height} viewBox={`0 0 ${CONNECTOR_W} ${height}`} data-id="element-431" data-mp="1ha1i4mgxwtzi">
          {nodes.map((n, ti) => n.surfaces.map(id => {
          const si = surfaces.findIndex(s => s.id === id);
          if (si < 0) return null;
          const y1 = centerY(ti, nodes.length);
          const y2 = centerY(si, surfaces.length);
          // Nothing is drawn at rest — every link at once was noise. Lines
          // appear only for the row under the pointer.
          const live = Boolean(focus) && isLive(n.slug, id);
          return <path key={`${n.slug}-${id}`} d={`M0 ${y1} C ${CONNECTOR_W * 0.45} ${y1}, ${CONNECTOR_W * 0.55} ${y2}, ${CONNECTOR_W} ${y2}`} fill="none" stroke="#EA580C" strokeWidth={1.6} opacity={live ? 1 : 0} style={{
            transition: 'opacity 160ms ease-out'
          }} data-id="element-432" data-mp="vgvw3ffgia4a" />;
        }))}
        </svg>
      </div>

      <ul className="space-y-2 md:w-[210px]" onMouseLeave={() => setFocus(null)} data-id="element-433" data-mp="gkobwf41t8p0">
        <ListLabel offset={(height - listH(surfaces.length)) / 2} data-id="element-434" data-mp="2fwqu7m0xrx13">
          AI surfaces
        </ListLabel>
        {surfaces.map(s => <li key={s.id} data-id="element-435" data-mp="2ayywuchivipi">
            <button type="button" onMouseEnter={() => setFocus({
          kind: 'surface',
          id: s.id
        })} onFocus={() => setFocus({
          kind: 'surface',
          id: s.id
        })} onClick={() => setFocus(f => f?.kind === 'surface' && f.id === s.id ? null : {
          kind: 'surface',
          id: s.id
        })} className={`flex w-full items-center gap-3 rounded-2xl border px-3.5 text-left transition-colors ${focus?.kind === 'surface' && focus.id === s.id ? 'border-[#EA580C]/45 bg-[#FFF8F3]' : 'border-[#EFEAE1] bg-white'} ${surfaceDim(s.id) ? 'opacity-40' : ''}`} style={{
          height: ROW_H
        }} data-id="element-436" data-mp="2ayp62monl7ir">
              <SurfaceIcon slug={s.slug} className="size-[18px] shrink-0" data-id="element-437" data-mp="29g7etphh7pkt" />
              <span className="min-w-0 flex-1" data-id="element-438" data-mp="2eftqvvlkizs1">
                <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-439" data-mp="2egmp286gogyk">
                  {s.name}
                </span>
                <span className="block truncate text-[11.5px] text-neutral-500" data-id="element-440" data-mp="3ky9skdidnend">
                  {nodes.filter(n => n.surfaces.includes(s.id)).length} tools ·{' '}
                  {s.note}
                </span>
              </span>
            </button>
          </li>)}
      </ul>
    </div>;
}
/** The label doubles as the spacer that vertically centres a shorter column. */
function ListLabel({
  children,
  offset = 0
}: {
  children: React.ReactNode;
  offset?: number;
}) {
  return <li className="flex items-start text-[10px] font-bold uppercase tracking-[0.1em] text-neutral-400" style={{
    height: LABEL_H + offset
  }} data-id="element-441" data-mp="356pho08cj7cw">
      {children}
    </li>;
}